import React, { useState, useMemo } from "react";
import { Table, Button, Spinner, Row, Col, Card } from "react-bootstrap";
import "../styles/tableAdmin.css";
import CustomPagination from "./CustomPagination";
import ItemsPerPageSelector from "./ItemsPerPageSelector";
import SearchBar from "./SearchBar"; // Import SearchBar

const TableAdmin = ({
  columns,
  data,
  handleEdit,
  handleDelete,
  hideActions = false, // Set default value to false
  customActions,
  loading, // Add loading prop
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "descending",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Change default to 10

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    let sortableItems = [...data].filter(Boolean);
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const filteredData = useMemo(() => {
    return sortedData.filter((item) => {
      if (!item) return false;
      return columns.some((col) => {
        const value = item[col.key];
        return value
          ? value.toString().toLowerCase().includes(searchQuery.toLowerCase())
          : false;
      });
    });
  }, [sortedData, searchQuery, columns]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? (
        <i className="bi bi-arrow-up ms-2"></i>
      ) : (
        <i className="bi bi-arrow-down ms-2"></i>
      );
    }
    return <i className="bi bi-arrow-down ms-2"></i>;
  };

  const entryInfo = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, filteredData.length);
    return `Showing ${start} to ${end} of ${filteredData.length} entries`;
  };

  return (
    <Card className="my-3 card-table-admin">
      <Card.Body className="py-4">
        <Row>
          <Col
            md={6}
            className="d-flex align-items-center justify-content-start"
          >
            <ItemsPerPageSelector
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </Col>
          <Col md={6} className="d-flex align-items-center justify-content-end">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12} className="mt-3">
            <Table striped bordered hover variant="light" className="table-admin" responsive>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => requestSort(col.key)}
                      className="sortable-column"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        {col.label}
                        {getSortIcon(col.key)}
                      </div>
                    </th>
                  ))}
                  {!hideActions && <th>Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + (hideActions ? 0 : 1)} className="text-center">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item.id || index}>
                      {columns.map((col) => (
                        <td
                          key={`${item.id}-${col.key}`}
                          className={`${
                            col.key === "email" ? "email-column" : ""
                          }`}
                        >
                          {col.key === "id"
                            ? (currentPage - 1) * itemsPerPage + index + 1
                            : item[col.key]}
                        </td>
                      ))}
                      {!hideActions && (
                        <td className="text-center d-flex gap-2">
                          {customActions ? customActions(item) : (
                            <>
                              <Button
                                className="btn-edit-user"
                                onClick={() => handleEdit(item)}
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </Button>
                              <Button
                                className="btn-delete-user"
                                onClick={() => handleDelete(item)}
                              >
                                <i className="bi bi-trash3-fill"></i>
                              </Button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length + (hideActions ? 0 : 1)} className="text-center">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
        <Row>
          <Col md={6} className="d-flex align-items-top">
            <p>{entryInfo()}</p>
          </Col>
          <Col md={6} className="d-flex justify-content-end">
            <CustomPagination
              totalPages={totalPages}
              currentPage={currentPage}
              handlePageChange={setCurrentPage}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default TableAdmin;
